import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Eye, EyeOff, Plus, Trash2, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  WORK_MODES,
  api,
  type DepartmentRef,
  type EmployeeSalary,
  type Paginated,
  type SalaryFigure,
  type WorkMode,
} from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMoney, isoDate } from '../lib/format';
import { generatePassword } from '../lib/passwords';
import { FormGrid, FormPage } from '../components/FormPage';
import { Button, Card, CardHeader, Field, Input, Select, useToast } from '../components/ui';

/** Two amounts are the same salary when they agree to the cent — the API's rule too. */
const sameAmount = (a: string | number, b: string | number) =>
  Math.round(Number(a) * 100) === Math.round(Number(b) * 100);

/** What became of a new employee's welcome email — the API's `welcomeEmail`. */
interface WelcomeEmail {
  status: 'QUEUED' | 'OFF' | 'NOT_CONFIGURED';
  to: string;
}

type MemberType = 'MEMBER' | 'LEADER';

/** One department on the edit form; an empty `departmentId` is a row not filled in. */
interface DepartmentRow {
  key: number;
  departmentId: string;
  memberType: MemberType;
}

let nextRowKey = 1;
const departmentRow = (departmentId = '', memberType: MemberType = 'MEMBER'): DepartmentRow => ({
  key: nextRowKey++,
  departmentId,
  memberType,
});

/** The same departments, Member or Leader alike, whatever their order. */
const sameDepartments = (a: Array<{ id: string; memberType: string }>, b: Array<{ id: string; memberType: string }>) =>
  a.map((d) => `${d.id}:${d.memberType}`).sort().join() === b.map((d) => `${d.id}:${d.memberType}`).sort().join();

/** An admin role as `GET /roles` sends it; `addOn` marks one held beside the one role, as Create tickets is. */
interface RoleOption {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  addOn?: boolean;
  permissions: Array<{ permission: { code: string } }>;
}

/** What each seeded role does, in a line; a role made through the API shows its name alone. */
const ROLE_HINTS: Record<string, TranslationKey> = {
  ADMIN: 'employees.roleHintAdmin',
  TEAM_LEADER: 'employees.roleHintLeader',
  TEAM_MEMBER: 'employees.roleHintMember',
  EMPLOYEE: 'employees.roleHintEmployee',
};
const ADD_ON_HINTS: Record<string, TranslationKey> = {
  TICKET_CREATOR: 'employees.addOnTicketsHint',
  DRIVE_SHARER: 'employees.addOnDriveHint',
};

/** Whether a role already grants everything an add-on would — an Administrator raises tickets anyway. */
const grantsAll = (role: RoleOption | undefined, addOn: RoleOption) =>
  !!role && addOn.permissions.every((p) => role.permissions.some((q) => q.permission.code === p.permission.code));

/**
 * Creating and editing an employee, as a page.
 *
 * Whoever adds an employee gives them a basic salary — it is required. After
 * that, only someone whose Finance roles handle payroll (`salaryAccess`) sees
 * it or changes it: the edit form shows the field to them alone, and the API
 * sends nobody else a figure (operator, 2026-10-05). A change is a new salary
 * record from the day chosen (§19.3); Finance keeps the history, and the rest
 * of the payroll terms, on its Salaries page. A salary booked for later that has
 * not started is changed in place instead — its figure, or the day it starts —
 * and one starting later with nothing in force says what that leaves out
 * (2026-10-07: a first salary typed as 31 Dec 2027 left an October penalty at 0).
 *
 * A new employee is emailed the admin's address, their username and the
 * password given here, in the language chosen here (operator, 2026-10-06).
 * When email is off or not set up, the toast says so, and whoever added them
 * hands the sign-in over themselves.
 *
 * The create form joins one department. The edit form lists every department
 * they are in, as Member or Leader, and changes them (operator, 2026-10-06) —
 * for whoever may do so on a department's own page (`departments.manage`). One
 * taken off is closed today, not deleted.
 *
 * An employee has one role, chosen from cards, and beside it the extra
 * permissions — Create tickets, without which only an administrator raises a
 * ticket (operator, 2026-10-07). Someone who held several roles before must be
 * given one before their roles save again.
 *
 * Routes: `/employees/new` and `/employees/:id/edit`.
 */
export function EmployeeFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handlesSalary = !!user?.salaryAccess;
  // A new employee's salary is given by whoever adds them; changing it is payroll's.
  const showsSalary = !editing || handlesSalary;
  // Creating someone always gives them roles; changing them later is roles.manage's.
  const managesRoles = !editing || can(P.ROLES_MANAGE);
  // The right a department's own page asks for to add or remove someone.
  const managesDepartments = editing && can(P.DEPARTMENTS_MANAGE);

  const [form, setForm] = useState({
    email: '', password: '', fullName: '', fullNameAr: '', employeeCode: '',
    jobTitle: '', phone: '', locale: 'en', departmentId: '', memberType: 'MEMBER',
    roleCodes: ['EMPLOYEE'] as string[],
    // At the office unless the form says otherwise, as the API makes them.
    workMode: 'OFFICE' as WorkMode,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [salary, setSalary] = useState({ basicSalary: '', effectiveFrom: isoDate() });
  const [departmentRows, setDepartmentRows] = useState<DepartmentRow[]>([]);

  const existing = useQuery({
    queryKey: ['user', id],
    queryFn: () => api.get<any>(`/users/${id}`),
    enabled: editing,
  });
  const { data: departments } = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () => api.get<Paginated<DepartmentRef>>('/departments', { pageSize: 100 }),
    enabled: !editing,
  });
  // Switched-off ones too: someone may still be in one, and it must show by name.
  const { data: departmentPicker } = useQuery({
    queryKey: ['departments', 'picker', 'all'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options', { includeInactive: true }),
    enabled: managesDepartments,
  });
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleOption[]>('/roles'),
    enabled: managesRoles,
  });
  // The currency a first salary is paid in — Finance's base currency.
  const defaults = useQuery({
    queryKey: ['users', 'salary-defaults'],
    queryFn: () => api.get<{ currency: string }>('/users/salary-defaults'),
    enabled: showsSalary,
  });

  const onFile = existing.data?.salary as EmployeeSalary | undefined;
  // The record a new figure follows: a salary booked for later, or the one in force.
  const latest: SalaryFigure | null = onFile?.scheduled ?? onFile?.current ?? null;
  // A salary booked for later has not started: the form changes its figure, or moves its day.
  const booked = editing && !!onFile?.scheduled;
  // The first day a change may start on; an older API sends none, and the latest record's day holds.
  const earliest = onFile?.earliestFrom !== undefined ? onFile.earliestFrom : latest?.effectiveFrom ?? null;
  const today = isoDate();

  // Seed the form once the record arrives; `loaded` keeps later edits intact.
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    const e = existing.data;
    setLoaded(true);
    setForm({
      ...form,
      email: e.email ?? '',
      fullName: e.fullName ?? '',
      fullNameAr: e.fullNameAr ?? '',
      employeeCode: e.employeeCode ?? '',
      jobTitle: e.jobTitle ?? '',
      phone: e.phone ?? '',
      roleCodes: (e.roles ?? []).map((r: { code: string }) => r.code),
      workMode: e.workMode ?? 'OFFICE',
    });
    // Someone in no department starts from one empty row, as the create form does.
    const rows = (e.departments ?? []).map((d: { id: string; memberType: MemberType }) => departmentRow(d.id, d.memberType));
    setDepartmentRows(rows.length ? rows : [departmentRow()]);
    setSalary({
      basicSalary: latest ? String(Number(latest.basicSalary)) : '',
      // Changing a booked raise corrects it on its own day; otherwise a change starts today.
      effectiveFrom: latest && latest.effectiveFrom > today ? latest.effectiveFrom : today,
    });
  }

  const currency = latest?.currency ?? defaults.data?.currency ?? '';
  const amount = Number(salary.basicSalary);
  const salaryChanged =
    salary.basicSalary.trim() !== '' && (!latest || !sameAmount(salary.basicSalary, latest.basicSalary));
  const dayMoved = booked && !!latest && !!salary.effectiveFrom && salary.effectiveFrom !== latest.effectiveFrom;
  // A new employee's salary is always sent; an edit sends a changed figure, or a booked salary's new day.
  const sendsSalary = showsSalary && (!editing || salaryChanged || dayMoved);
  const salaryProblem =
    sendsSalary &&
    (!(amount > 0) || !salary.effectiveFrom || (editing && !!earliest && salary.effectiveFrom < earliest));
  // With nothing in force today, a salary that starts later leaves payroll and penalties nothing to go on until then.
  const startsLater =
    showsSalary && (!editing || salaryChanged || booked) && salary.effectiveFrom > today && !onFile?.current;

  // An employee code is given once (operator, 2026-10-06): one left empty when
  // they were added can be given here, and once saved it is shown, not offered.
  const codeLocked = editing && !!existing.data?.employeeCode?.trim();

  // One role, and the add-ons beside it. Someone may still hold several roles
  // from before the rule: then none is chosen until the form is given one.
  const mainRoles = (roles ?? []).filter((r) => !r.addOn);
  const addOnRoles = (roles ?? []).filter((r) => r.addOn);
  const chosenRoles = mainRoles.filter((r) => form.roleCodes.includes(r.code));
  const chosenRole = chosenRoles.length === 1 ? chosenRoles[0] : undefined;
  const roleName = (r: { name: string; nameAr: string | null }) => (locale === 'ar' ? r.nameAr ?? r.name : r.name);
  const chooseRole = (code: string) =>
    setForm({ ...form, roleCodes: [code, ...form.roleCodes.filter((c) => addOnRoles.some((r) => r.code === c))] });
  const toggleAddOn = (code: string, on: boolean) =>
    setForm({ ...form, roleCodes: on ? [...form.roleCodes, code] : form.roleCodes.filter((c) => c !== code) });
  // An add-on the role already grants is not kept: it would change nothing.
  const roleCodesToSend = form.roleCodes.filter((code) => {
    const addOn = addOnRoles.find((r) => r.code === code);
    return !addOn || !grantsAll(chosenRole, addOn);
  });
  // Judged once the roles arrive; before that, a new employee is an Employee, as the API makes them.
  const roleMissing = managesRoles && !!roles && !chosenRole;

  const rolesChanged =
    !editing ||
    [...roleCodesToSend].sort().join() !==
      (existing.data?.roles ?? []).map((r: { code: string }) => r.code).sort().join();

  // The departments they are in show by name before the picker arrives, and if it ever leaves one out.
  const heldDepartments: Array<DepartmentRef & { memberType: MemberType }> = existing.data?.departments ?? [];
  const knownDepartments: DepartmentRef[] = [
    ...(departmentPicker ?? []),
    ...heldDepartments.filter((d) => !departmentPicker?.some((o) => o.id === d.id)),
  ];
  const taken = (id: string) => departmentRows.some((r) => r.departmentId === id);
  // A row offers its own department, and every active one no row has yet.
  const choicesFor = (row: DepartmentRow) =>
    knownDepartments.filter((d) => d.id === row.departmentId || (d.isActive !== false && !taken(d.id)));
  const chosenDepartments = departmentRows.filter((r) => r.departmentId);
  const departmentsChanged =
    managesDepartments &&
    !sameDepartments(chosenDepartments.map((r) => ({ id: r.departmentId, memberType: r.memberType })), heldDepartments);
  // One empty row at a time, and only while a department is left to choose.
  const canAddDepartment =
    chosenDepartments.length === departmentRows.length && knownDepartments.some((d) => d.isActive !== false && !taken(d.id));
  const changeRow = (key: number, patch: Partial<DepartmentRow>) =>
    setDepartmentRows(departmentRows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: number) => {
    const rest = departmentRows.filter((r) => r.key !== key);
    setDepartmentRows(rest.length ? rest : [departmentRow()]);
  };

  const save = useMutation({
    mutationFn: () => {
      const salaryBody = sendsSalary ? { basicSalary: amount, effectiveFrom: salary.effectiveFrom } : undefined;
      if (!editing) {
        return api.post<any>('/users', {
          email: form.email.trim(),
          password: form.password,
          fullName: form.fullName.trim(),
          fullNameAr: form.fullNameAr.trim() || undefined,
          employeeCode: form.employeeCode.trim() || undefined,
          jobTitle: form.jobTitle.trim() || undefined,
          workMode: form.workMode,
          phone: form.phone.trim() || undefined,
          locale: form.locale,
          departmentId: form.departmentId || undefined,
          memberType: form.memberType,
          roleCodes: roles ? roleCodesToSend : form.roleCodes,
          salary: salaryBody,
        });
      }
      // An emptied field is cleared, not left as it was — except the employee
      // code, which is only ever given, never changed or taken away.
      return api.patch<any>(`/users/${id}`, {
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        fullNameAr: form.fullNameAr.trim() || null,
        employeeCode: codeLocked ? undefined : form.employeeCode.trim() || undefined,
        jobTitle: form.jobTitle.trim() || null,
        workMode: form.workMode,
        phone: form.phone.trim() || null,
        roleCodes: managesRoles && roles && rolesChanged ? roleCodesToSend : undefined,
        // Every department they should be in — sent only when that changed.
        departments: departmentsChanged
          ? chosenDepartments.map((r) => ({ departmentId: r.departmentId, memberType: r.memberType }))
          : undefined,
        salary: salaryBody,
      });
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['user', saved.id] });
      if (departmentsChanged) {
        void queryClient.invalidateQueries({ queryKey: ['department'] });
        void queryClient.invalidateQueries({ queryKey: ['departments'] });
      }
      if (!editing) announceWelcome(saved);
      navigate(`/employees/${saved.id}`);
    },
    onError: (e: any) => {
      if (e?.code === 'EMPLOYEE_CODE_LOCKED') return toast.error(t('employees.codeLockedError'));
      if (e?.code === 'DUPLICATE_RESOURCE' && e?.details?.field === 'employeeCode') {
        return toast.error(t('employees.codeTaken', { code: form.employeeCode.trim() }));
      }
      if (e?.code === 'VALIDATION_FAILED' && e?.details?.field === 'roleCodes') {
        return toast.error(t('employees.chooseOneRole'));
      }
      toast.error(e.message);
    },
  });

  /** Whether the welcome email went: if not, whoever added them gives them their sign-in. */
  const announceWelcome = (saved: { fullName: string; fullNameAr?: string | null; email: string; welcomeEmail?: WelcomeEmail }) => {
    const vars = { name: localized(saved), email: saved.welcomeEmail?.to ?? saved.email };
    switch (saved.welcomeEmail?.status) {
      case 'QUEUED':
        return toast.success(t('employees.welcomeQueued', vars));
      case 'OFF':
        return toast.toast(t('employees.welcomeOff', vars));
      case 'NOT_CONFIGURED':
        return toast.toast(t('employees.welcomeNotConfigured', vars));
    }
  };

  const name = existing.data ? localized(existing.data) : '';
  const money = (figure: SalaryFigure) => formatMoney(figure.basicSalary, figure.currency, locale);

  return (
    <FormPage
      title={editing ? `${t('common.edit')} — ${name}` : t('employees.new')}
      backTo={editing ? `/employees/${id}` : '/employees'}
      backLabel={editing ? name || t('employees.title') : t('nav.employees')}
      width="lg"
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={
        !form.email.trim() ||
        !form.fullName.trim() ||
        (!editing && !form.password) ||
        roleMissing ||
        salaryProblem
      }
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={`${t('common.name')} (EN)`} required>
          <Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('common.name')} (AR)`}>
          <Input dir="rtl" value={form.fullNameAr} onChange={(e) => setForm({ ...form, fullNameAr: e.target.value })} />
        </Field>
        <Field label={t('common.email')} required>
          <Input type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        {!editing && (
          <Field label={t('auth.passwordLabel')} required hint={t('employees.passwordHint')}>
            <div className="flex gap-2">
              <Input
                type={showPassword ? 'text' : 'password'}
                dir="ltr"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="min-w-0 flex-1"
              />
              <Button
                icon={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                aria-label={showPassword ? t('employees.passwordHide') : t('employees.passwordShow')}
                title={showPassword ? t('employees.passwordHide') : t('employees.passwordShow')}
                onClick={() => setShowPassword(!showPassword)}
              />
              <Button
                onClick={() => {
                  setForm({ ...form, password: generatePassword() });
                  setShowPassword(true);
                }}
              >
                {t('employees.passwordGenerate')}
              </Button>
            </div>
          </Field>
        )}
        <Field label={t('employees.code')} hint={codeLocked ? t('employees.codeLocked') : t('employees.codeOnce')}>
          <Input
            dir="ltr"
            value={form.employeeCode}
            onChange={(e) => setForm({ ...form, employeeCode: e.target.value })}
            disabled={codeLocked}
          />
        </Field>
        <Field label={t('employees.jobTitle')}>
          <Input value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
        </Field>
        <Field label={t('employees.workMode')} hint={t('employees.workModeHint')}>
          <Select value={form.workMode} onChange={(e) => setForm({ ...form, workMode: e.target.value as WorkMode })}>
            {WORK_MODES.map((mode) => (
              <option key={mode} value={mode}>{t(`employees.workMode.${mode}`)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.phone')}>
          <Input type="tel" dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        {!editing && (
          <>
            <Field label={t('employees.language')} hint={t('employees.languageHint')}>
              <Select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}>
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </Select>
            </Field>
            <Field label={t('common.department')}>
              <Select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })}>
                <option value="">—</option>
                {departments?.items.map((d) => (
                  <option key={d.id} value={d.id}>{localized(d)}</option>
                ))}
              </Select>
            </Field>
            {/* The department's Member or Leader; the role below is the employee's own. */}
            <Field label={t('employees.departmentRole')}>
              <Select value={form.memberType} onChange={(e) => setForm({ ...form, memberType: e.target.value })}>
                <option value="MEMBER">{enumLabel('MEMBER', locale)}</option>
                <option value="LEADER">{enumLabel('LEADER', locale)}</option>
              </Select>
            </Field>
          </>
        )}

        {managesDepartments && (
          <fieldset className="sm:col-span-2">
            <legend className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">
              {t('departments.title')}
            </legend>
            <ul className="space-y-4 sm:space-y-2">
              {departmentRows.map((row) => (
                // On a phone the department takes the first line; Member/Leader and remove share the second.
                <li key={row.key} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 sm:grid-cols-[minmax(0,1fr)_10rem_auto]">
                  <Select
                    className="col-span-2 sm:col-span-1"
                    aria-label={t('common.department')}
                    value={row.departmentId}
                    onChange={(e) => changeRow(row.key, { departmentId: e.target.value })}
                  >
                    <option value="">—</option>
                    {choicesFor(row).map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.isActive === false ? `${localized(d)} (${t('settings.switchedOff')})` : localized(d)}
                      </option>
                    ))}
                  </Select>
                  <Select
                    aria-label={t('employees.departmentRole')}
                    value={row.memberType}
                    onChange={(e) => changeRow(row.key, { memberType: e.target.value as MemberType })}
                  >
                    <option value="MEMBER">{enumLabel('MEMBER', locale)}</option>
                    <option value="LEADER">{enumLabel('LEADER', locale)}</option>
                  </Select>
                  <Button
                    icon={<Trash2 className="h-4 w-4" />}
                    aria-label={t('employees.removeDepartment')}
                    title={t('employees.removeDepartment')}
                    disabled={departmentRows.length === 1 && !row.departmentId}
                    onClick={() => removeRow(row.key)}
                  />
                </li>
              ))}
            </ul>
            <Button
              className="mt-2"
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              disabled={!canAddDepartment}
              onClick={() => setDepartmentRows([...departmentRows, departmentRow()])}
            >
              {t('employees.addDepartment')}
            </Button>
            <p className="mt-1.5 text-caption text-faint">{t('employees.departmentsEditHint')}</p>
          </fieldset>
        )}

        {managesRoles && (
          <fieldset className="sm:col-span-2">
            <legend className="mb-1.5 flex items-center gap-1 text-body-sm font-medium text-[var(--text-secondary)]">
              {t('employees.role')}
              <span className="text-primary dark:text-primary-400">*</span>
            </legend>
            {chosenRoles.length > 1 && (
              <p className="mb-2 rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
                {t('employees.severalRoles', { roles: chosenRoles.map(roleName).join(locale === 'ar' ? '، ' : ', ') })}
              </p>
            )}
            <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('employees.role')}>
              {mainRoles.map((r) => {
                const chosen = chosenRole?.code === r.code;
                const hint = ROLE_HINTS[r.code];
                return (
                  <label
                    key={r.id}
                    className={clsx(
                      'flex cursor-pointer gap-2.5 rounded-md border px-3 py-2.5 transition-colors duration-fast',
                      chosen ? 'border-primary bg-primary/8' : 'hairline hover:bg-[var(--surface-sunken)]',
                    )}
                  >
                    <input
                      type="radio"
                      name="employee-role"
                      value={r.code}
                      className="mt-1 accent-primary"
                      checked={chosen}
                      onChange={() => chooseRole(r.code)}
                    />
                    <span className="min-w-0">
                      <span className="block text-body-sm font-medium text-[var(--text-primary)]">{roleName(r)}</span>
                      {hint && <span className="block text-caption text-muted">{t(hint)}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}

        {managesRoles && addOnRoles.length > 0 && (
          <fieldset className="sm:col-span-2">
            <legend className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">
              {t('employees.extraPermissions')}
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {addOnRoles.map((r) => {
                // The role grants it already: shown ticked, and not a choice.
                const included = grantsAll(chosenRole, r);
                const on = included || form.roleCodes.includes(r.code);
                const hint = ADD_ON_HINTS[r.code];
                return (
                  <label
                    key={r.id}
                    className={clsx(
                      'flex gap-2.5 rounded-md border px-3 py-2.5 transition-colors duration-fast',
                      on ? 'border-primary bg-primary/8' : 'hairline hover:bg-[var(--surface-sunken)]',
                      included ? 'cursor-not-allowed' : 'cursor-pointer',
                    )}
                  >
                    <input
                      type="checkbox"
                      value={r.code}
                      className="mt-1 h-4 w-4 accent-primary"
                      checked={on}
                      disabled={included}
                      onChange={(e) => toggleAddOn(r.code, e.target.checked)}
                    />
                    <span className="min-w-0">
                      <span className="block text-body-sm font-medium text-[var(--text-primary)]">{roleName(r)}</span>
                      <span className="block text-caption text-muted">
                        {included && chosenRole
                          ? t('employees.addOnIncluded', { role: roleName(chosenRole) })
                          : hint
                            ? t(hint)
                            : null}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}
      </FormGrid>

      {showsSalary && (
        <Card className="mt-5">
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden />
                {t('employees.salary')}
              </span>
            }
            subtitle={editing ? t('employees.salaryHint') : t('employees.salaryCreateHint')}
          />
          <FormGrid>
            <Field
              label={currency ? `${t('employees.basicSalary')} (${currency})` : t('employees.basicSalary')}
              required={!editing}
              hint={
                !editing ? (
                  t('employees.salaryNewHint')
                ) : (
                  <span className="block space-y-0.5">
                    <span className="block">
                      {onFile?.current
                        ? t('employees.salaryNow', {
                            amount: money(onFile.current),
                            date: formatDate(onFile.current.effectiveFrom, locale),
                          })
                        : t('employees.salaryNone')}
                    </span>
                    {onFile?.scheduled && (
                      <span className="block">
                        {t('employees.salaryNext', {
                          amount: money(onFile.scheduled),
                          date: formatDate(onFile.scheduled.effectiveFrom, locale),
                        })}
                      </span>
                    )}
                  </span>
                )
              }
            >
              <Input
                type="number"
                inputMode="decimal"
                min={0.01}
                step="0.01"
                dir="ltr"
                value={salary.basicSalary}
                onChange={(e) => setSalary({ ...salary, basicSalary: e.target.value })}
              />
            </Field>
            {(!editing || salaryChanged || booked) && (
              <Field
                label={t('employees.effectiveFrom')}
                required
                hint={
                  !editing
                    ? t('employees.salaryFirstHint')
                    : booked
                      ? t('employees.salaryBookedHint')
                      : t('employees.salaryFromHint')
                }
              >
                <Input
                  type="date"
                  min={editing ? earliest ?? undefined : undefined}
                  value={salary.effectiveFrom}
                  onChange={(e) => setSalary({ ...salary, effectiveFrom: e.target.value })}
                />
              </Field>
            )}
          </FormGrid>
          {startsLater && (
            <p className="mt-4 rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
              {t('employees.salaryStartsLater', { date: formatDate(salary.effectiveFrom, locale) })}
            </p>
          )}
        </Card>
      )}
    </FormPage>
  );
}
